import type { VoicePreset } from '../domain/types'

export interface SampleNote {
  name: string
  midi: number
}

export interface SampleVoice {
  folder: string
  notes: SampleNote[]
}

const NOTE_OFFSETS: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }

export function noteNameToMidi(name: string): number {
  const match = /^([A-G])(s|#|b)?(-?\d)$/.exec(name)
  if (!match) throw new Error(`Unrecognized note name: ${name}`)
  const [, letter, accidental, octave] = match
  const offset = NOTE_OFFSETS[letter!]! + (accidental === 's' || accidental === '#' ? 1 : accidental === 'b' ? -1 : 0)
  return (Number(octave) + 1) * 12 + offset
}

function voice(folder: string, names: string[]): SampleVoice {
  return { folder, notes: names.map((name) => ({ name, midi: noteNameToMidi(name) })) }
}

// VSCO 2 Community Edition samples (CC0) via nbrosowsky/tonejs-instruments,
// thinned to one sample every 4-7 semitones; the sampler pitch-shifts between.
export const SAMPLE_VOICES: Partial<Record<VoicePreset, SampleVoice>> = {
  harp: voice('harp', ['C3', 'G3', 'D4', 'A4', 'E5']),
  piano: voice('piano', ['C3', 'E3', 'Gs3', 'C4', 'E4', 'Gs4', 'C5', 'E5', 'Gs5']),
  'music-box': voice('music-box', ['C5', 'G5', 'C6', 'G6', 'C7', 'G7', 'C8'])
}

export function isSampledVoice(preset: VoicePreset): boolean {
  return preset in SAMPLE_VOICES
}

export function samplePath(voice: SampleVoice, note: SampleNote): string {
  return `/samples/${voice.folder}/${note.name}.mp3`
}

// Picks the closest recorded note and the playback rate that retunes it.
export function nearestSample(midi: number, notes: readonly SampleNote[]): { note: SampleNote; rate: number } {
  if (notes.length === 0) throw new Error('No samples available')
  let best = notes[0]!
  for (const note of notes) if (Math.abs(note.midi - midi) < Math.abs(best.midi - midi)) best = note
  return { note: best, rate: 2 ** ((midi - best.midi) / 12) }
}
